import type { OrderType, ThemeType, IWidgetTheme, LabelStyle, IndicatorStyle} from '@wscsports/blaze-web-sdk';
import type { BlazeWidgetAPIResponse, WidgetConfig, BlazeEventHandlers } from './types';
import blazeSDKManager from './blaze-sdk-manager';

type LayoutMap = Record<string, 'WidgetRowView' | 'WidgetGridView' | 'WidgetEmbeddedStory'>

export const LAYOUT_TO_FACTORY: Readonly<LayoutMap> = Object.freeze({
  'row-circle': 'WidgetRowView',
  'row-rectangle': 'WidgetGridView',
  'grid-2-columns': 'WidgetGridView',
  'grid-3-columns': 'WidgetGridView',
  'row-rectangle-horizontal': 'WidgetGridView',
  'grid-2-columns-horizontal': 'WidgetGridView',
  'grid-3-columns-horizontal': 'WidgetGridView',
  'row-rectangle-animated': 'WidgetGridView',
  'embedded-story': 'WidgetEmbeddedStory'
});

/**
 * Map search page sort_by param to Blaze SDK OrderType
 * @param {string} sortByParam - Sort param from search (e.g., '-start_time', 'start_time')
 * @returns {OrderType|undefined} - Blaze OrderType or undefined for relevance (default)
 */
export function mapSearchSortToBlazeOrder(sortByParam?: string): OrderType | undefined {
  if (!sortByParam) return undefined;
  
  const mapping: Record<string, OrderType> = {
    '-start_time': 'RecentlyCreatedFirst',
    'start_time': 'RecentlyCreatedLast',
  };
  
  return mapping[sortByParam] || null;
}

function sanitizeStyleIndicatorObject(obj: Partial<IndicatorStyle> | undefined): Partial<IndicatorStyle> {
  const defaultStyle: Partial<IndicatorStyle> = {
    isVisible: false,
    text: '',
  };

  if (!obj) return defaultStyle;

  const cleaned = Object.fromEntries(
    Object.entries({ ...defaultStyle, ...obj }).filter(([, v]) => v !== undefined)
  );

  return cleaned as Partial<IndicatorStyle>;
}

function sanitizeLabelStyleObject(obj: Partial<LabelStyle> | undefined): Partial<LabelStyle> {
  const defaultLabelStyle: Partial<LabelStyle> = {
    isVisible: true,
    gap: 8,
    width: 'auto',
    padding: '0px',
    position: 'outsideBottom',
    height: '20px',
    fontSize: '14px',
    fontWeight: 'bold',
  };

  if (!obj) return defaultLabelStyle;

  return {
    ...defaultLabelStyle,
    ...obj,
    gap: obj.gap !== undefined && !isNaN(Number(obj.gap)) ? Number(obj.gap) : defaultLabelStyle.gap,
  };
}

export function determineWidgetType(layout: string): 'WidgetRowView' | 'WidgetGridView' | 'WidgetEmbeddedStory' {
  return (LAYOUT_TO_FACTORY as LayoutMap)[layout] || 'WidgetRowView';
}

/**
 * Builds label expressions for Blaze SDK from label groups
 * Handles both OR (multiple groups) and AND (labels within a group) logic
 * @returns Object with groupExpressions and priorityExpressions arrays
 */
export function buildLabelExpressions(
  labelGroups: Array<{ id: string; andLabels: string[] }>,
  priorityGroups: Array<{ id: string; andLabels: string[] }> = []
): { groupExpressions: any[]; priorityExpressions: any[] } {
  const sdk = blazeSDKManager.getSDK();
  
  if (!sdk) {
    throw new Error('Blaze SDK not initialized');
  }

  // Build main label groups (OR logic between groups, AND within groups)
  const groupExpressions: any[] = [];
  labelGroups.forEach(group => {
    if (group.andLabels.length === 1) {
      groupExpressions.push(sdk.LabelBuilder().singleLabel(group.andLabels[0]));
    } else if (group.andLabels.length > 1) {
      const [firstLabel, ...otherLabels] = group.andLabels;
      const baseLabel = sdk.LabelBuilder().singleLabel(firstLabel);
      const otherLabelExpressions = otherLabels.map(label => sdk.LabelBuilder().singleLabel(label));
      groupExpressions.push(sdk.LabelBuilder().mustInclude(baseLabel, ...otherLabelExpressions));
    }
  });

  // Build priority label expressions
  const priorityExpressions = priorityGroups
    .filter(group => group.andLabels.length > 0)
    .map(group => {
      if (group.andLabels.length === 1) {
        return sdk.LabelBuilder().singleLabel(group.andLabels[0]);
      }
      const [firstLabel, ...otherLabels] = group.andLabels;
      const baseLabel = sdk.LabelBuilder().singleLabel(firstLabel);
      const otherLabelExpressions = otherLabels.map(label => sdk.LabelBuilder().singleLabel(label));
      return sdk.LabelBuilder().mustInclude(baseLabel, ...otherLabelExpressions);
    });

  return { groupExpressions, priorityExpressions };
}

/**
 * Builds a labels data source for Blaze SDK
 * @param labelGroups - Main label groups (OR logic)
 * @param priorityGroups - Priority label groups (boosting)
 * @param options - Additional options (text search, orderType, maxItems)
 */
export function buildLabelsDataSource(
  labelGroups: Array<{ id: string; andLabels: string[] }>,
  priorityGroups: Array<{ id: string; andLabels: string[] }>,
  options: {
    text?: string;
    orderType?: OrderType;
    maxItems?: number;
  } = {}
): any {
  const sdk = blazeSDKManager.getSDK();
  
  if (!sdk) {
    throw new Error('Blaze SDK not initialized');
  }

  const { groupExpressions, priorityExpressions } = buildLabelExpressions(labelGroups, priorityGroups);

  if (groupExpressions.length === 0) {
    throw new Error('No valid label groups provided');
  }

  const finalLabelExpression = groupExpressions.length === 1
    ? groupExpressions[0]
    : sdk.LabelBuilder().atLeastOneOf(...groupExpressions);

  return sdk.DataSourceBuilder().labels({
    labels: finalLabelExpression,
    ...(options.text ? { text: options.text } : {}),
    labelsPriority: priorityExpressions.length > 0 ? priorityExpressions : undefined,
    orderType: options.orderType,
    maxItems: options.maxItems
  } as any);
}

export function convertAPIResponseToConfig(apiResponse: BlazeWidgetAPIResponse): WidgetConfig {
  const settings = apiResponse.settings;
  const normalizedLayout = (Object.keys(LAYOUT_TO_FACTORY) as string[]).includes(settings.layout) ? settings.layout : 'default';
  return {
    containerId: apiResponse.containerId || `blaze-widget-${crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2, 15)}`,
    widgetType: apiResponse.type,
    apiKey: settings.apiToken,
    contentType: settings.contentType,
    layout: settings.layout,
    themeLayout: normalizedLayout,
    maxItems: settings.maxItems,
    maxDisplayItems: settings.maxDisplayItems,
    style: settings.style,
    selector: settings.selector,
    height: settings.height || '300px',
    width: settings.width || '100%',
    isGeoBased: settings.isGeoBased
  };
}

function applyStyleOrHide(
  layoutStyle: any,
  styleKey: string,
  styleConfig: any,
  sanitizer: (obj: any) => any
): void {
  if (styleConfig?.[styleKey] && styleConfig[styleKey].isVisible) {
    layoutStyle[styleKey] = sanitizer(styleConfig[styleKey]);
  } else if (layoutStyle[styleKey]) {
    layoutStyle[styleKey].isVisible = false;
  }
}

export function applyThemeConfiguration(theme: any, styleConfig: any): void {
  const layoutStyle = theme.layoutStyle;
  if (!layoutStyle) return;
  if (!styleConfig || typeof styleConfig !== 'object') return;

  const simpleProps = [
    'shouldAllowHorizontalStories',
    'horizontalItemsSpacing',
    'verticalItemsSpacing',
    'borderWidth',
    'borderRadius',
    'borderPadding',
    'badgePosition',
    'statusIndicatorPosition'
  ];

  simpleProps.forEach(prop => {
    if (styleConfig[prop] !== undefined) {
      layoutStyle[prop] = styleConfig[prop];
    }
  });

  applyStyleOrHide(layoutStyle, 'labelStyle', styleConfig, sanitizeLabelStyleObject);
  applyStyleOrHide(layoutStyle, 'statusLiveStyle', styleConfig, sanitizeStyleIndicatorObject);
  applyStyleOrHide(layoutStyle, 'statusLiveUnreadStyle', styleConfig, sanitizeStyleIndicatorObject);
  applyStyleOrHide(layoutStyle, 'statusReadStyle', styleConfig, sanitizeStyleIndicatorObject);
  applyStyleOrHide(layoutStyle, 'statusUnreadStyle', styleConfig, sanitizeStyleIndicatorObject);
  applyStyleOrHide(layoutStyle, 'badgeIndicatorStyle', styleConfig, sanitizeStyleIndicatorObject);
}

export function buildSDKConfig(config: WidgetConfig, options?: {
  hideContainerWhenEmpty?: boolean;
  delegates?: Record<string, (e: any) => void>;
  eventHandlers?: BlazeEventHandlers;
}): any {
  const sdk = blazeSDKManager.getSDK();

  if (!sdk) {
    throw new Error('Blaze SDK not initialized');
  }

  const effectiveOrderType = (config.orderBy ? mapSearchSortToBlazeOrder(config.orderBy) : config.selector.sortBy) as OrderType;
  
  let dataSource: any;
  if (config.selector.source === 'labels' && config.selector.labels) {
    const labelGroups = config.selector.labels.labelGroups || [];
    const priorityGroups = config.selector.labels.priorityGroups || [];
    dataSource = buildLabelsDataSource(labelGroups, priorityGroups, {
      text: config.searchText,
      orderType: effectiveOrderType,
      maxItems: config.maxItems
    });
  } else if (config.searchText) {
    dataSource = (sdk.DataSourceBuilder() as any).text({
      text: config.searchText,
      orderType: effectiveOrderType,
      maxItems: config.maxItems
    });
  } else if (config.selector.source === 'content' && config.selector.content?.items?.length) {
    dataSource = sdk.DataSourceBuilder().ids({
      ids: config.selector.content.items,
      orderType: effectiveOrderType,
    });
  } else {
    throw new Error('Invalid selector configuration: must provide either valid content items, label groups, or search text');
  }

  const normalizedLayout = (config.themeLayout || 'default') as ThemeType;
  let theme: IWidgetTheme;
  if (config.contentType === 'moment') {
    theme = sdk.Theme(normalizedLayout, 'moment');
  } else if (config.contentType === 'video') {
    theme = sdk.Theme(normalizedLayout, 'video');
  } else {
    theme = sdk.Theme(normalizedLayout, 'story');
  }
  if (config.style) {
    applyThemeConfiguration(theme, config.style);
  }
  if (config.layout === 'row-rectangle-animated') {
    theme.layoutStyle.thumbnailType = 'GIF';
  }


  const defaultDelegates = {
    [sdk.Delegations.onWidgetDataLoadCompleted]: (e: any) => {
      if (options?.hideContainerWhenEmpty !== false && e.detail.contentCount === 0) {
        const container = typeof document !== 'undefined' ? document.getElementById(config.containerId) : null;
        if (container) container.style.display = 'none';
      }
      if (options?.eventHandlers?.onDataLoadCompleted) {
        options.eventHandlers.onDataLoadCompleted({
          contentCount: e.detail.contentCount,
          containerId: config.containerId,
          eventType: 'data-load-completed-blaze'
        });
      }
    },
  };
  const delegates = { ...defaultDelegates, ...(options?.delegates || {}) };

  if (options?.eventHandlers?.onWidgetItemClick) {
    sdk.setOnItemClick((itemData: {
      containerId: string;
      item: any;
      itemIndexInContainer: number;
      contentType: string;
      url?: string;
    }) => {
      itemData.url = `/#blaze/${itemData.contentType}/${itemData.item.id}`;
      options.eventHandlers?.onWidgetItemClick?.(itemData);
      // Build internal URL and forward to handler
      return { shouldPreventOpen: config.requireAuth || false }; 
    });
  }

  return {
    dataSource,
    theme,
    maxDisplayItemsCount: config.maxDisplayItems,
    contentType: config.contentType,
    shouldOrderWidgetByReadStatus: true,
    delegates
  };
}


